import { AppstoreOutlined } from "@ant-design/icons"
import { Button, Card, Empty, Flex, Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { TemplateTypeCard } from "./TemplateTypeCard.tsx"
import { TEMPLATE_TYPES } from "./template-types.ts"
import { useTemplateCounts } from "./use-template-counts.ts"

const { Text, Title } = Typography

const GRID_CLASS = "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"

export function SettingsTemplatesHub() {
  const { t } = useTranslation()
  const permissions = useAppSelector(
    (state) => state?.auth?.userInfo?.permissions?.codes?.settings
  )
  const { counts, reload } = useTemplateCounts(permissions)

  const loading = TEMPLATE_TYPES.some(
    (type) => counts[type.key]?.status === "loading"
  )
  // Show a card for every reachable type — "ready" (render its count, even 0,
  // so the type stays creatable) or "error" (render a retryable error). Types
  // that are unavailable (no permission, or not enabled for the workspace) are
  // hidden, since there is nothing there to reach.
  const shown = TEMPLATE_TYPES.filter((type) => {
    const status = counts[type.key]?.status
    return status === "ready" || status === "error"
  })

  const header = (
    <Flex align={"center"} gap={"middle"}>
      <div
        aria-hidden
        className={
          "flex size-12 shrink-0 items-center justify-center rounded-lg bg-purple-50"
        }
      >
        <AppstoreOutlined className={"text-2xl text-purple-500"} />
      </div>
      <Flex vertical>
        <Title className={"mb-0 text-start"} level={2}>
          {t("settingsTemplatesHub.title")}
        </Title>
        <Text type={"secondary"}>{t("settingsTemplatesHub.subtitle")}</Text>
      </Flex>
    </Flex>
  )

  const body = () => {
    if (loading) {
      return (
        <div className={GRID_CLASS}>
          {TEMPLATE_TYPES.map((type) => (
            <Skeleton.Node
              active
              className={"h-[168px] w-full"}
              key={type.key}
            />
          ))}
        </div>
      )
    }

    if (!shown.length) {
      return (
        <Card>
          <Empty description={t("settingsTemplatesHub.empty")} />
        </Card>
      )
    }

    return (
      <div className={GRID_CLASS}>
        {shown.map((type) => {
          const state = counts[type.key]

          if (state.status === "error") {
            return (
              <Card
                className={"h-full rounded-lg"}
                classNames={{ body: "flex h-full flex-col gap-3 p-4" }}
                key={type.key}
              >
                <Flex align={"flex-start"} gap={"middle"}>
                  <div
                    aria-hidden
                    className={
                      "flex size-12 shrink-0 items-center justify-center rounded-lg bg-slate-100"
                    }
                  >
                    <type.Icon className={"text-2xl text-slate-400"} />
                  </div>
                  <Flex vertical gap={4}>
                    <Title className={"mb-0 font-semibold"} level={5}>
                      {t(type.titleKey)}
                    </Title>
                    <Text type={"secondary"}>
                      {t("settingsTemplatesHub.card.loadError")}
                    </Text>
                  </Flex>
                </Flex>
                <Button
                  className={"mt-auto self-start"}
                  onClick={reload}
                  size={"small"}
                >
                  {t("settingsTemplatesHub.card.retry")}
                </Button>
              </Card>
            )
          }

          return (
            <TemplateTypeCard
              Icon={type.Icon}
              accent={type.accent}
              chips={type.chipKeys?.map((key) => t(key))}
              count={state.status === "ready" ? state.count : 0}
              countKey={type.countKey}
              description={t(type.descriptionKey)}
              key={type.key}
              title={t(type.titleKey)}
              to={type.route}
            />
          )
        })}
      </div>
    )
  }

  return (
    <Flex className={"w-full"} gap={"large"} vertical>
      {header}
      {body()}
    </Flex>
  )
}
